import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessao } from '../auth/sessao';
import { inicial } from '../api/tokens';
import { useTema } from '../tema';
import {
  IconePaleta, IconeSair, IconeTour, IconeTutorial, Logo, Lua, Lupa, Sol,
} from '../componentes/Marca';
import type { Projeto } from '../api/tipos';

export function Topbar({ slug, projetos, busca, aoBuscar, aoAbrirPaleta, aoAbrirTour }: {
  slug: string | null;
  projetos: Projeto[];
  busca: string;
  aoBuscar: (q: string) => void;
  aoAbrirPaleta: () => void;
  aoAbrirTour: () => void;
}) {
  const { eu, sair } = useSessao();
  const { alternar } = useTema();
  const navegar = useNavigate();
  const campo = useRef<HTMLInputElement>(null);

  // "/" cai na busca, como no GitHub — sem roubar a tecla de quem está
  // escrevendo. É o mesmo atalho de app.js:711-717, agora preso à vida do
  // componente em vez de a um listener que nunca era removido.
  useEffect(() => {
    function noTeclado(e: KeyboardEvent) {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const alvo = e.target as HTMLElement | null;
      const tag = alvo?.tagName || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || alvo?.isContentEditable) return;
      e.preventDefault();
      campo.current?.focus();
      campo.current?.select();
    }
    document.addEventListener('keydown', noTeclado);
    return () => document.removeEventListener('keydown', noTeclado);
  }, []);

  return (
    <div className="topbar">
      <div className="brand">
        <div className="logo"><Logo /></div>
        <div>context engine<small>knowledge platform</small></div>
      </div>

      <div className="search">
        <Lupa classe="ico" />
        <label className="sr-only" htmlFor="q">Busca semântica no acervo</label>
        <input
          id="q" ref={campo} aria-keyshortcuts="/"
          placeholder="Busca semântica — ex: como envia mensagem via SERPRO   ( / )"
          value={busca}
          onChange={(e) => aoBuscar(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && slug) navegar(`/p/${slug}/search?q=${encodeURIComponent(busca)}`); }}
        />
      </div>

      <div className="topbar-right">
        <label className="sr-only" htmlFor="proj">Projeto</label>
        <select
          className="proj-select" id="proj"
          value={slug ?? ''}
          onChange={(e) => navegar(`/p/${e.target.value}`)}
          disabled={!projetos.length}
        >
          {projetos.length
            ? projetos.map((p) => <option key={p.slug} value={p.slug}>{p.slug} · {p.docs} docs</option>)
            : <option value="">(sem projetos delegados)</option>}
        </select>

        <button className="icon-btn theme-btn" type="button" title="Tema" onClick={alternar}>
          <Lua /><Sol />
        </button>
        <button
          className="icon-btn" type="button" id="palette-btn"
          title="Ir para… (Ctrl+K)" aria-keyshortcuts="Control+K"
          onClick={aoAbrirPaleta}
        >
          <IconePaleta />
        </button>
        <button
          className="icon-btn" type="button" id="tour-btn"
          title="Tour guiado do painel"
          onClick={aoAbrirTour}
        >
          <IconeTour />
        </button>
        <a className="icon-btn" href="/tutorial" target="_blank" rel="noreferrer" title="Tutorial animado: o ciclo completo">
          <IconeTutorial />
        </a>

        <div className="userchip">
          <span className="avatar">{inicial(eu?.email || '')}</span>
          <span id="u-email" className="muted">{eu?.email}</span>
          <span className="role">{eu?.role}</span>
        </div>
        <button className="icon-btn" type="button" title="Sair" onClick={sair}><IconeSair /></button>
      </div>
    </div>
  );
}
